import React from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  VaultOnboardingForm,
  VaultOnboardingFormValues,
  OwnerOption,
} from '../components/VaultOnboardingForm';
import { vaultApiRef } from '../api/VaultApi';

// How often to poll OPA reachability while the form is mounted — frequent
// enough to clear the banner soon after a real recovery, not so frequent
// it hammers OPA just to check whether it's down.
const OPA_HEALTH_POLL_INTERVAL_MS = 15_000;

// ScaffolderFormValues maps users from OwnerOption[] to string[] because
// vault:generate-manifest expects users: string[] (email IDs), not OwnerOption objects.
export interface ScaffolderFormValues extends Omit<
  VaultOnboardingFormValues,
  'users'
> {
  users: string[];
}

export function mapToScaffolderValues(
  values: VaultOnboardingFormValues,
): ScaffolderFormValues {
  return { ...values, users: values.users.map(u => u.id) };
}

const STUB_BSN_OPTIONS = [
  // 7777–7779: pre-seeded in Vault — use for "edit existing" flow testing.
  { id: 'BSN-7777', label: 'BSN-7777 (existing)' },
  { id: 'BSN-7778', label: 'BSN-7778 (existing)' },
  { id: 'BSN-7779', label: 'BSN-7779 (existing)' },
  // 8881–8890: NOT seeded in Vault — use for "new onboarding" flow testing.
  { id: 'BSN-8881', label: 'BSN-8881 (new)' },
  { id: 'BSN-8882', label: 'BSN-8882 (new)' },
  { id: 'BSN-8883', label: 'BSN-8883 (new)' },
  { id: 'BSN-8884', label: 'BSN-8884 (new)' },
  { id: 'BSN-8885', label: 'BSN-8885 (new)' },
  { id: 'BSN-8886', label: 'BSN-8886 (new)' },
  { id: 'BSN-8887', label: 'BSN-8887 (new)' },
  { id: 'BSN-8888', label: 'BSN-8888 (new)' },
  { id: 'BSN-8889', label: 'BSN-8889 (new)' },
  { id: 'BSN-8890', label: 'BSN-8890 (new)' },
];

type BsnStub = {
  techLead: string;
  managers: string[];
  approvers: string[];
  users: OwnerOption[];
};

const BSN_STUBS: Record<string, BsnStub> = {
  'BSN-8881': {
    techLead: 'Jordan Lee',
    managers: ['Sam Torres'],
    approvers: ['Platform Security Group', 'Architecture Review Board'],
    users: [
      { id: 'j.lee@example.com', label: 'Jordan Lee', type: 'user' },
      { id: 's.torres@example.com', label: 'Sam Torres', type: 'user' },
      { id: 'k.nguyen@example.com', label: 'Kim Nguyen', type: 'user' },
    ],
  },
  'BSN-8882': {
    techLead: 'Devon Osei',
    managers: ['Fatima Malik'],
    approvers: ['Data Governance Council', 'Platform Security Group'],
    users: [
      { id: 'd.osei@example.com', label: 'Devon Osei', type: 'user' },
      { id: 'f.malik@example.com', label: 'Fatima Malik', type: 'user' },
      { id: 'c.russo@example.com', label: 'Chris Russo', type: 'user' },
    ],
  },
  'BSN-8883': {
    techLead: 'Yuki Tanaka',
    managers: ['Ines Ferreira'],
    approvers: ['Infrastructure Security', 'Architecture Review Board'],
    users: [
      { id: 'y.tanaka@example.com', label: 'Yuki Tanaka', type: 'user' },
      { id: 'i.ferreira@example.com', label: 'Ines Ferreira', type: 'user' },
      { id: 'b.chen@example.com', label: 'Bo Chen', type: 'user' },
    ],
  },
  'BSN-8884': {
    techLead: 'Clara Voss',
    managers: ['Raj Patel'],
    approvers: ['Platform Security Group', 'Architecture Review Board'],
    users: [
      { id: 'c.voss@example.com', label: 'Clara Voss', type: 'user' },
      { id: 'r.patel@example.com', label: 'Raj Patel', type: 'user' },
      { id: 'n.kim@example.com', label: 'Nina Kim', type: 'user' },
    ],
  },
  'BSN-8885': {
    techLead: 'Omar Hassan',
    managers: ['Leila Zhao'],
    approvers: ['Data Governance Council', 'Platform Security Group'],
    users: [
      { id: 'o.hassan@example.com', label: 'Omar Hassan', type: 'user' },
      { id: 'l.zhao@example.com', label: 'Leila Zhao', type: 'user' },
      { id: 'g.silva@example.com', label: 'Gabriel Silva', type: 'user' },
    ],
  },
  'BSN-8886': {
    techLead: 'Amara Diallo',
    managers: ['Tomas Novak'],
    approvers: ['Infrastructure Security', 'Architecture Review Board'],
    users: [
      { id: 'a.diallo@example.com', label: 'Amara Diallo', type: 'user' },
      { id: 't.novak@example.com', label: 'Tomas Novak', type: 'user' },
      { id: 'e.jensen@example.com', label: 'Erik Jensen', type: 'user' },
    ],
  },
  'BSN-8887': {
    techLead: 'Sofia Reyes',
    managers: ['Daniel Wu'],
    approvers: ['Platform Security Group', 'Data Governance Council'],
    users: [
      { id: 's.reyes@example.com', label: 'Sofia Reyes', type: 'user' },
      { id: 'd.wu@example.com', label: 'Daniel Wu', type: 'user' },
      { id: 'h.obi@example.com', label: 'Hannah Obi', type: 'user' },
    ],
  },
  'BSN-8888': {
    techLead: 'Lucas Bauer',
    managers: ['Mei Lin'],
    approvers: ['Architecture Review Board', 'Infrastructure Security'],
    users: [
      { id: 'l.bauer@example.com', label: 'Lucas Bauer', type: 'user' },
      { id: 'm.lin@example.com', label: 'Mei Lin', type: 'user' },
      { id: 'a.petrov@example.com', label: 'Alex Petrov', type: 'user' },
    ],
  },
  'BSN-8889': {
    techLead: 'Zara Ahmed',
    managers: ['Kenji Mori'],
    approvers: ['Platform Security Group', 'Architecture Review Board'],
    users: [
      { id: 'z.ahmed@example.com', label: 'Zara Ahmed', type: 'user' },
      { id: 'k.mori@example.com', label: 'Kenji Mori', type: 'user' },
      { id: 'v.santos@example.com', label: 'Valentina Santos', type: 'user' },
    ],
  },
  'BSN-8890': {
    techLead: 'Ibrahim Yilmaz',
    managers: ['Chloe Dupont'],
    approvers: ['Data Governance Council', 'Infrastructure Security'],
    users: [
      { id: 'i.yilmaz@example.com', label: 'Ibrahim Yilmaz', type: 'user' },
      { id: 'c.dupont@example.com', label: 'Chloe Dupont', type: 'user' },
      { id: 'p.garcia@example.com', label: 'Pablo Garcia', type: 'user' },
    ],
  },
  'BSN-7777': {
    techLead: 'Alice Chen',
    managers: ['David Park'],
    approvers: ['Platform Security Group', 'Architecture Review Board'],
    users: [
      { id: 'a.chen@example.com', label: 'Alice Chen', type: 'user' },
      { id: 'd.park@example.com', label: 'David Park', type: 'user' },
      { id: 's.patel@example.com', label: 'Sanjay Patel', type: 'user' },
    ],
  },
  'BSN-7778': {
    techLead: 'Marcus Rivera',
    managers: ['Laura Kim'],
    approvers: ['Data Governance Council', 'Platform Security Group'],
    users: [
      { id: 'm.rivera@example.com', label: 'Marcus Rivera', type: 'user' },
      { id: 'l.kim@example.com', label: 'Laura Kim', type: 'user' },
      { id: 'j.okafor@example.com', label: 'James Okafor', type: 'user' },
    ],
  },
  'BSN-7779': {
    techLead: 'Priya Nair',
    managers: ['Tom Walsh'],
    approvers: ['Infrastructure Security', 'Architecture Review Board'],
    users: [
      { id: 'p.nair@example.com', label: 'Priya Nair', type: 'user' },
      { id: 't.walsh@example.com', label: 'Tom Walsh', type: 'user' },
      { id: 'r.santos@example.com', label: 'Rosa Santos', type: 'user' },
    ],
  },
};

export function VaultOnboardingFormField(
  props: FieldExtensionComponentProps<ScaffolderFormValues>,
) {
  const { onChange, formData } = props;
  const bsnStub = formData?.bsns?.[0] ? BSN_STUBS[formData.bsns[0]] : undefined;
  const vaultApi = useApi(vaultApiRef);
  const [opaDown, setOpaDown] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const poll = () => {
      vaultApi
        .checkOpaHealth()
        .then(({ reachable }) => {
          if (!cancelled) setOpaDown(!reachable);
        })
        .catch(() => {
          if (!cancelled) setOpaDown(true);
        });
    };
    poll();
    const timer = setInterval(poll, OPA_HEALTH_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [vaultApi]);

  const handleValuesChange = React.useCallback(
    (values: VaultOnboardingFormValues) => {
      onChange(mapToScaffolderValues(values));
    },
    [onChange],
  );

  return (
    <VaultOnboardingForm
      bsnOptions={STUB_BSN_OPTIONS}
      userOptions={bsnStub?.users ?? []}
      techLead={bsnStub?.techLead}
      managers={bsnStub?.managers}
      approvers={bsnStub?.approvers}
      initialValues={formData ? { ...formData, users: [] } : undefined}
      onValuesChange={handleValuesChange}
      opaDown={opaDown}
    />
  );
}
